<template>
  <div
      class="television-option-card io-eq-el"
      :class="{
        small,
      }"
  >
    <div
        class="television-option-card__title"
        data-eq-part="television-option-card__title"
    >
      <div class="television-option-card__title-name">{{ option.name }}</div>
    </div>
    <div
        class="television-option-card__benefits"
        data-eq-part="television-option-card__benefits"
    >
      <List
          type="icon"
          icon="fa-solid fa-check"
          :items="optionProConList"
          :small="small"
      />
    </div>
    <div class="television-option-card__price" data-eq-part="television-option-card__price">
      <div>+{{ monthlyBasePrice }}&thinsp;€/Monat</div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import List from '@/components/List.vue';
import { formatNumber } from '@/helpers';
import TelevisionPackageInterface from '@/interfaces/TelevisionPackageInterface';
import { computed } from 'vue';

const props = defineProps<{
  option: TelevisionPackageInterface
  small?: boolean
}>();
const monthlyBasePrice = computed(() => formatNumber(props.option.price?.recurring.find((recurringPrice) => recurringPrice?.type === 'base_price')?.value || 0, 2));

const optionProConList = computed(() => [
  ...(props.option.benefits || []),
  ...((props.option.cons || []).map((con: string | string[]) => [
    Array.isArray(con) ? con[0] : con,
    Array.isArray(con) ? con[1] : undefined,
    'fa-solid fa-xmark',
  ])),
]);
</script>

<style lang="scss">
@import "src/scss/component-helpers";

.television-option-card {
  box-shadow: 0 3px 6px rgba(0, 0, 0, .15);
  border-radius: 20px;
  background-color: #ffffff;

  @media(min-width: map-get($grid-breakpoints, 'lg')) {
    border-radius: 40px;
  }

  &.small {
    border-radius: 10px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .15);
  }

  &__title {
    display: grid;
    grid-template-columns: auto;
    justify-content: center;
    text-align: center;
    padding: 30px 20px 0;
    align-items: center;

    @media(min-width: map-get($grid-breakpoints, 'lg')) {
      padding: 30px 40px 0;
      justify-content: start;
      text-align: left;
    }

    .small > & {
      padding: 20px 10px 0;

      @media(min-width: map-get($grid-breakpoints, 'lg')) {
        padding: 20px 30px 0;
      }
    }

    &-name {
      font-size: 1rem;
      font-weight: 700;
      line-height: 1.5;

      @media(min-width: map-get($grid-breakpoints, 'lg')) {
        font-size: 1.5rem;
      }

      .small > div > & {
        font-size: 1rem;

        @media (min-width: map_get($grid-breakpoints, 'lg')) {
          font-size: 1.125rem;
        }
      }
    }
  }

  &__benefits {
    text-align: left;
    padding: 20px 20px;

    @media(min-width: map-get($grid-breakpoints, 'lg')) {
      padding: 20px 40px;
    }

    .small > & {
      @media(min-width: map-get($grid-breakpoints, 'lg')) {
        padding: 20px 30px;
      }
    }
  }

  &__price {
    padding: 10px 20px;
    background-color: #ECF2F6;
    text-align: center;
    font-size: 1rem;
    font-weight: 700;
    border-radius: 0 0 20px 20px;

    @media(min-width: map-get($grid-breakpoints, 'lg')) {
      font-size: 1.25rem;
      border-radius: 0 0 40px 40px;
      padding: 20px 40px;
    }

    .small > & {
      font-size: 1rem;
      border-radius: 0 0 10px 10px;
      padding: 10px 20px;

      @media (min-width: map_get($grid-breakpoints, 'lg')) {
        font-size: 1.125rem;
        padding: 15px 30px;
      }
    }
  }

  &.selected {
    position: relative;

    &:after {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: 20px;
      border: 2px solid #2B257E;
      pointer-events: none;
      z-index: 4;

      @media(min-width: map-get($grid-breakpoints, 'lg')) {
        border-radius: 40px;
      }
    }

    &.small:after {
      border-radius: 10px;
    }
  }

  &.disabled {
    position: relative;

    &:after {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: 20px;
      background-color: rgba(255, 255, 255, .7);
      z-index: 4;

      @media(min-width: map-get($grid-breakpoints, 'lg')) {
        border-radius: 40px;
      }
    }

    &.small:after {
      border-radius: 10px;
    }
  }
}
</style>
